import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  CardActionArea,
  IconButton,
  Popover,
  ClickAwayListener,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Lock as LockIcon } from '@mui/icons-material';
import { Thought } from '../services/thoughtService';
import LabelSelector from './LabelSelector';

interface ThoughtCardProps {
  thought: Thought;
  availableLabels: string[];
  onLabelsUpdate: (thoughtId: string, labels: string[]) => void;
}

const ThoughtCard: React.FC<ThoughtCardProps> = ({ thought, availableLabels, onLabelsUpdate }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const labelButtonRef = useRef<HTMLButtonElement>(null);

  // Check if thought is locked (decryption failed)
  const isLocked = thought.title === '🔒 Locked';

  const getPreview = (content: string): string => {
    if (!content) return 'No content';
    return content.length > 150 ? content.substring(0, 150) + '...' : content;
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const handleCardClick = () => {
    if (!isLocked) {
      navigate(`/thought/${thought.id}`);
    }
  };

  const handleLabelButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isLocked) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLabelsChange = (newLabels: string[]) => {
    onLabelsUpdate(thought.id, newLabels);
  };

  const open = Boolean(anchorEl);

  return (
    <Card
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: isLocked ? 'warning.main' : 'divider',
        borderRadius: 2,
        transition: 'all 0.2s',
        opacity: isLocked ? 0.7 : 1,
        '&:hover': {
          boxShadow: isLocked ? 1 : 3,
          borderColor: isLocked ? 'warning.main' : 'primary.light',
        }
      }}
    >
      <CardActionArea
        onClick={handleCardClick}
        disabled={isLocked}
        sx={{ flexGrow: 1, alignItems: 'flex-start' }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {isLocked && <LockIcon sx={{ color: 'warning.main', fontSize: 20 }} />}
            <Typography 
              variant="h6" 
              component="h2" 
              sx={{ 
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: 1.4,
                flexGrow: 1,
              }}
            >
              {isLocked ? 'Locked Thought' : thought.title}
            </Typography>
          </Box>
          
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ 
              flexGrow: 1, 
              mb: 2,
              fontSize: '0.875rem',
              lineHeight: 1.6,
              fontStyle: isLocked ? 'italic' : 'normal',
            }}
          >
            {isLocked 
              ? 'This thought is encrypted with a different passkey'
              : getPreview(thought.content)
            }
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 0.5,
                alignItems: 'center',
                minHeight: 32,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isLocked && thought.labels && thought.labels.map((label, index) => (
                <Chip
                  key={index}
                  label={label}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              ))}
              
              {!isLocked && (
                <IconButton
                  ref={labelButtonRef}
                  size="small"
                  onClick={handleLabelButtonClick}
                  sx={{
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {formatDate(thought.updatedAt)}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: '90vw',
              p: 2,
              mt: 1,
              overflow: 'visible',
            }
          }
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Add Labels
            </Typography>
            <LabelSelector
              availableLabels={availableLabels}
              selectedLabels={thought.labels || []}
              onLabelsChange={handleLabelsChange}
            />
          </Box>
        </ClickAwayListener>
      </Popover>
    </Card>
  );
};

export default ThoughtCard;
